import type { paths } from '@cou/contracts';
import type { Verdict as DisplayVerdict } from '@cou/admin-ui';

/*
 * The admin catalogue client (API-16 browse; API-08 and API-18 sync triggers). Every call is
 * bearer-authenticated (the first authenticated GETs in the app — API-15's client only ever
 * POSTs). Mirrors admin-auth-client's shape: a discriminated result keyed off the failure
 * `code` rather than the HTTP status.
 */

type EventsBody =
  paths['/api/admin/catalogue/events']['get']['responses'][200]['content']['application/json'];
type MarketsBody =
  paths['/api/admin/catalogue/events/{id}/markets']['get']['responses'][200]['content']['application/json'];
type SyncBody =
  paths['/api/admin/catalogue/sync']['post']['responses'][200]['content']['application/json'];
type MetadataSyncBody =
  paths['/api/admin/metadata/sync']['post']['responses'][200]['content']['application/json'];
type SyncRunsBody =
  paths['/api/admin/sync-runs']['get']['responses'][200]['content']['application/json'];

export type CatalogueEvent = EventsBody['events'][number];
export type CatalogueMarket = MarketsBody['markets'][number];
/** The latest run of one sync (CAE-469): last-sync time + outcome, fed to the status cards. */
export type SyncRun = NonNullable<SyncRunsBody['catalogue']>;

/** API-16's GL-17 verdict (`out_of_bounds`) as admin-ui's `VerdictBadge` spells it (`out-of-bounds`). */
export function toDisplayVerdict(verdict: CatalogueMarket['verdict']): DisplayVerdict {
  return verdict === 'out_of_bounds' ? 'out-of-bounds' : verdict;
}

export type CatalogueResult<T> =
  | ({ ok: true } & T)
  | { ok: false; status: number; code: string | undefined; message: string | undefined };

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

function authHeaders(token: string): HeadersInit {
  return { authorization: `Bearer ${token}` };
}

async function failure(
  response: Response,
): Promise<{ status: number; code: string | undefined; message: string | undefined }> {
  const body = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;
  return { status: response.status, code: body?.error?.code, message: body?.error?.message };
}

/** A rejected fetch (offline, DNS, CORS) — no HTTP response to read a status/code from. */
const NETWORK_FAILURE = {
  ok: false as const,
  status: 0,
  code: 'network_error',
  message: undefined,
};

async function getJson<T>(url: string, token: string): Promise<CatalogueResult<T>> {
  const response = await fetch(url, { headers: authHeaders(token) }).catch(() => null);
  if (response === null) return NETWORK_FAILURE;
  if (response.ok) {
    return { ok: true, ...((await response.json()) as T) };
  }
  return { ok: false, ...(await failure(response)) };
}

async function postJson<T>(url: string, token: string, body: unknown): Promise<CatalogueResult<T>> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(body),
  }).catch(() => null);
  if (response === null) return NETWORK_FAILURE;
  if (response.ok) {
    return { ok: true, ...((await response.json()) as T) };
  }
  return { ok: false, ...(await failure(response)) };
}

/** API-16 — the browsable Live-catalogue Events (allowlisted `MATCH` events), optionally sport-scoped. */
export function listEvents(token: string, sport?: string): Promise<CatalogueResult<EventsBody>> {
  const query = sport !== undefined ? `?sport=${encodeURIComponent(sport)}` : '';
  return getJson(`${apiBase()}/api/admin/catalogue/events${query}`, token);
}

/** API-16 — an Event's over/under Markets, carrying quote and the GL-17 verdict. */
export function listMarkets(token: string, eventId: string): Promise<CatalogueResult<MarketsBody>> {
  return getJson(`${apiBase()}/api/admin/catalogue/events/${eventId}/markets`, token);
}

/** API-08 — pull Events, Markets, and Outcomes from the Sports Catalogue (currently a stub). */
export function syncCatalogue(token: string): Promise<CatalogueResult<SyncBody>> {
  return postJson(`${apiBase()}/api/admin/catalogue/sync`, token, {});
}

/** API-18 — refresh the team/athlete metadata registries. */
export function syncMetadata(token: string): Promise<CatalogueResult<MetadataSyncBody>> {
  return postJson(`${apiBase()}/api/admin/metadata/sync`, token, {});
}

/** CAE-469 — the latest sync run per kind (last-sync time + outcome), for the status cards. */
export function getSyncRuns(token: string): Promise<CatalogueResult<SyncRunsBody>> {
  return getJson(`${apiBase()}/api/admin/sync-runs`, token);
}
